// @flow
import { createSelector } from "reselect"
import type { InputSelector } from "reselect"
import { OrderedSet } from "immutable"
import * as R from "ramda"

import {
  getSymbol,
  getCurrencyName,
  formatPrice,
  formatSeparatePrice,
  formatISO4217Price,
} from "./currencies"
import convertPrice from "../price/convertPrice"
import { getLanguageInfo, getCountryCurrencies } from "../staticData/index"
import { getCurrenciesKeys } from "./index"
// Consider move to sheet / nest
// https://docs.google.com/spreadsheets/d/1PZt_6sVxpJyA-ub54j0PhBB5lHFkRoAh1qq8yxN_ga8/edit?ts=58d4d081
const MOST_USED_CURRENCIES = ["usd", "eur", "gbp", "aud", "sek", "dkk"]
type State = any

export const currenciesSelector = (state: State) => state.currencies
const countrySelector = state => state.options.country

export const currencySelector: InputSelector<State, *, string> = state => state.options.currency

// $FlowFixMe
export const currenciesKeysSelector = createSelector(
  [currenciesSelector, getCurrenciesKeys],
  (allCurrencies, whitelistedCurrencies: string[]) =>
    Object.keys(R.pick(whitelistedCurrencies.sort(), allCurrencies)),
)

// $FlowFixMe
export const getSymbolSelector = createSelector(
  [currenciesSelector],
  currencies => currencyCode => getSymbol(currencyCode, currencies),
)

// $FlowFixMe
export const getNameSelector = createSelector(
  currenciesSelector,
  currencies => currencyCode => getCurrencyName(currencyCode, currencies),
)

// $FlowFixMe
export const roundAmountSelector = createSelector(
  [currenciesSelector],
  currencies => (amount, currencyCode) => {
    const precision = currencies[currencyCode] ? currencies[currencyCode].precision : 2
    return amount.toFixed(precision)
  },
)

export type FormatPrice = (
  currencyCode: string,
  price: number,
  formatThousandsAndDecimals?: boolean,
  separatePrice?: boolean,
) => string
export const formatPriceSelector: InputSelector<State, *, FormatPrice> = createSelector(
  [currenciesSelector, getLanguageInfo, roundAmountSelector],
  (currencies, langInfo, roundAmount): FormatPrice => (
    currencyCode,
    price,
    formatThousandsAndDecimals = true,
    separatePrice = false,
  ) => {
    const floatPrice = parseFloat(price)
    const finalPrice =
      Math.round(floatPrice) !== floatPrice
        ? roundAmount(floatPrice, currencyCode)
        : String(floatPrice)

    const opts = {
      currencyCode,
      price: finalPrice,
      currencies,
      langInfo,
      formatThousandsAndDecimals,
    }

    if (separatePrice) {
      return formatSeparatePrice(opts)
    }
    return formatPrice(opts)
  },
)

// $FlowFixMe
export const formatPriceISO4217Selector = createSelector(
  [currenciesSelector, getLanguageInfo, currencySelector, roundAmountSelector],
  (currencies, langInfo, currency, roundAmount) => (
    currencyCode,
    price,
    formatThousandsAndDecimals = true,
  ) => {
    const floatPrice = parseFloat(price)
    const finalPrice =
      Math.round(floatPrice) !== floatPrice ? roundAmount(floatPrice, currency) : String(floatPrice)

    const opts = {
      currencyCode,
      price: finalPrice,
      currencies,
      langInfo,
      formatThousandsAndDecimals,
    }

    return formatISO4217Price(opts)
  },
)

// $FlowFixMe
export const convertPriceSelector = createSelector(
  [currenciesSelector],
  currencies => (currency, price) => convertPrice(price, currency, currencies),
)

export type GetFormattedPrice = (
  price: number,
  round?: boolean,
  separatePrice?: boolean,
  formatThousandsAndDecimals?: boolean,
) => string
export const getFormattedPriceSelector: InputSelector<State, *, GetFormattedPrice> = createSelector(
  [currencySelector, convertPriceSelector, formatPriceSelector],
  (currency, getConvertedPrice, getFormattedPrice) => (
    price,
    round,
    separatePrice,
    formatThousandsAndDecimals,
  ) => {
    const priceInCurrency = getConvertedPrice(currency, price)
    return getFormattedPrice(
      currency,
      round ? Math.round(priceInCurrency) : priceInCurrency,
      formatThousandsAndDecimals,
      separatePrice,
    )
  },
)

// $FlowFixMe
export const getRecommendedCurrenciesSelector = createSelector(
  [countrySelector, getCurrenciesKeys, getLanguageInfo, getCountryCurrencies],
  (country, whitelistedCurrencies, languageInfo, countryCurrencies) => {
    let recommendedCurrencies = OrderedSet()

    // Detect from GeoIP
    const countryData = countryCurrencies[country]

    if (countryData && whitelistedCurrencies.includes(countryData)) {
      recommendedCurrencies = recommendedCurrencies.add(countryData)
    }
    return (
      recommendedCurrencies
        // add currency by language
        .add(languageInfo.currency)
        // add currencies with biggest revenue and union them with previous
        .union(MOST_USED_CURRENCIES.filter(x => whitelistedCurrencies.includes(x)))
        .take(4)
    )
  },
)

// $FlowFixMe
export const getRecommendedCurrenciesSelectorJS = createSelector(
  getRecommendedCurrenciesSelector,
  data => data.toJS(),
)

// $FlowFixMe
export const getConvertedPriceSelector = createSelector(
  [currencySelector, convertPriceSelector],
  (currency, getConvertedPrice) => price => getConvertedPrice(currency, price),
)
